import { ApprovalConditions } from "shared/app/ExplainRecommendationModal/components/ApprovalConditions";
import { DeclineConditions } from "shared/app/ExplainRecommendationModal/components/DeclineConditions";
import { Header } from "shared/app/ExplainRecommendationModal/components/Header";
import { VerticalSpacer } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/components/VerticalSpacer";
import { RuleSetDecision } from "shared/app/VerificationTemplate/slices/DecisionCriteria/slices/RuleCriteria/types";
import { Badge, BadgeSize, BadgeVariant, Modal, Show } from "shared/components";
import { Card } from "shared/components/Card";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";

import { Application } from "types/application";
import { SkippedReportsOverview } from "shared/app/SkippedReportsOverview";

interface ExplainRecommendationModalProps {
  application: Application;
  opened: boolean;
  onClose: () => void;
}

export const ExplainRecommendationModal = ({
  application,
  opened,
  onClose
}: ExplainRecommendationModalProps) => {
  const ruleReasons = application.rule_reasons || [];

  const isMetDeclineConditions = ruleReasons.some((ruleSet) => {
    if (ruleSet.rule_set_type !== RuleSetDecision.Decline) return false;

    const activeRules = ruleSet.rules.filter((rule) => !rule.skipped);
    return (
      activeRules.length > 0 && activeRules.every((el) => el.result === true)
    );
  });

  const isMetApprovalConditions = ruleReasons.some((ruleSet) => {
    if (ruleSet.rule_set_type !== RuleSetDecision.Approve) return false;

    const activeRules = ruleSet.rules.filter((rule) => !rule.skipped);
    return (
      activeRules.length > 0 && activeRules.every((el) => el.result === true)
    );
  });

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="overflow-hidden max-h-[90vh] bg-background-900 h-full max-w-[1002px] w-full animate-appear border border-alpha-100 shadow-lg"
    >
      <Header onClose={onClose} />

      <Show
        if={
          (application.requires_manual_or_conditional_execution
            ?.total_verifications || 0) > 0
        }
      >
        <div className="px-8 py-4 bg-background-1000 border-b border-alpha-100">
          <SkippedReportsOverview
            applicationId={application.id}
            onClose={onClose}
            requiresManualExecution={
              application.requires_manual_or_conditional_execution
            }
          />
        </div>
      </Show>

      <div className="w-full p-6 overflow-auto h-full max-h-[calc(90vh-96px)]">
        <DeclineConditions
          application={application}
          isMetDeclineConditions={isMetDeclineConditions}
        />

        <Show if={!isMetDeclineConditions}>
          <VerticalSpacer height={24} className="ml-[118px]" />
          <Badge
            className="ml-4"
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
          >
            Conditions for decline weren&apos;t met
          </Badge>
          <VerticalSpacer height={24} className="ml-[118px]" />
          <ApprovalConditions
            application={application}
            isMetAnyApprovalConditions={isMetApprovalConditions}
          />
        </Show>

        <Show if={!isMetDeclineConditions && !isMetApprovalConditions}>
          <VerticalSpacer height={24} className="ml-[118px]" />
          <Badge
            className="ml-4"
            variant={BadgeVariant.BASE}
            size={BadgeSize.SM}
          >
            Conditions for approval weren&apos;t met
          </Badge>
          <VerticalSpacer height={24} className="ml-[118px]" />
          <Card>
            <div className="px-6 py-5 flex items-center gap-2">
              <SuccessIcon className="w-5 text-warning-500" />
              <h2 className="text-base font-bold text-content-1000">
                Recommended conditional approval
              </h2>
            </div>
          </Card>
        </Show>
      </div>
    </Modal>
  );
};
